export type PdfOrientation = 'portrait' | 'landscape'

/** A4 page size in CSS pixels (96dpi). */
const PAGE_PX: Record<PdfOrientation, { width: number; height: number }> = {
  portrait: { width: 794, height: 1123 },
  landscape: { width: 1123, height: 794 },
}

const MAX_CANVAS_HEIGHT = 14000

interface ExportOptions {
  fileName: string
  orientation: PdfOrientation
  background: string
  /** Top and bottom page margin in CSS pixels. */
  marginY?: number
}

export function pdfPageWidth(orientation: PdfOrientation) {
  return PAGE_PX[orientation].width
}

/**
 * Captures an element and saves it as a paginated A4 PDF. Pages break at the
 * top of descendants marked with `data-pdf-block`, so blocks are never cut.
 */
export async function exportElementToPdf(
  element: HTMLElement,
  { fileName, orientation, background, marginY = 56 }: ExportOptions,
) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ])

  const page = PAGE_PX[orientation]
  const contentHeight = page.height - marginY * 2
  const totalHeight = element.offsetHeight
  const slices = paginate(collectBreaks(element), totalHeight, contentHeight)
  const scale = Math.max(1, Math.min(2, MAX_CANVAS_HEIGHT / totalHeight))

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: background,
    useCORS: true,
    logging: false,
    onclone: (_doc: Document, cloned: HTMLElement) => revealForCapture(cloned),
  })

  const ratio = canvas.height / totalHeight
  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' })
  const pdfWidth = pdf.internal.pageSize.getWidth()
  const pdfHeight = pdf.internal.pageSize.getHeight()
  const k = pdfWidth / page.width

  slices.forEach(([start, end], i) => {
    const sliceHeight = Math.max(1, Math.round((end - start) * ratio))
    const sliceCanvas = document.createElement('canvas')
    sliceCanvas.width = canvas.width
    sliceCanvas.height = sliceHeight
    const ctx = sliceCanvas.getContext('2d')
    if (!ctx) throw new Error('Canvas unavailable')
    ctx.fillStyle = background
    ctx.fillRect(0, 0, sliceCanvas.width, sliceHeight)
    ctx.drawImage(
      canvas,
      0,
      Math.round(start * ratio),
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    )

    if (i > 0) pdf.addPage()
    pdf.setFillColor(background)
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F')
    pdf.addImage(
      sliceCanvas.toDataURL('image/jpeg', 0.92),
      'JPEG',
      0,
      marginY * k,
      pdfWidth,
      (end - start) * k,
    )
  })

  pdf.save(fileName)
}

function collectBreaks(root: HTMLElement) {
  const blocks = root.querySelectorAll<HTMLElement>('[data-pdf-block]')
  return Array.from(blocks).map((block) => offsetWithin(block, root))
}

function offsetWithin(element: HTMLElement, root: HTMLElement) {
  let top = 0
  let current: HTMLElement | null = element
  while (current && current !== root) {
    top += current.offsetTop
    const parent = current.offsetParent as HTMLElement | null
    if (parent && parent !== root && !root.contains(parent)) break
    current = parent
  }
  return top
}

function paginate(breaks: number[], total: number, pageHeight: number) {
  const points = Array.from(new Set(breaks))
    .filter((b) => b > 0 && b < total)
    .sort((a, b) => a - b)
  const slices: Array<[number, number]> = []
  let start = 0

  while (total - start > 1) {
    const limit = start + pageHeight
    if (limit >= total) {
      slices.push([start, total])
      break
    }
    const candidates = points.filter(
      (p) => p > start + pageHeight * 0.25 && p <= limit,
    )
    const end = candidates.length ? candidates[candidates.length - 1] : limit
    slices.push([start, end])
    start = end
  }

  return slices
}

/** Makes the staged copy visible and settles any scroll-triggered entrances. */
function revealForCapture(cloned: HTMLElement) {
  cloned.style.visibility = 'visible'
  cloned.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    if (el.style.opacity === '0') {
      el.style.opacity = '1'
      el.style.transform = 'none'
    }
  })
}
